import { useEffect, useState } from "react";
import { Plus, Trash2, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SEO } from "@/components/SEO";

const EMPTY = { customer_name: "", text: "", rating: 5, artist_slug: "", event_type: "", approved: true };

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [artists, setArtists] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/admin/artists").then((r) => setArtists(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/reviews", { ...form, rating: Number(form.rating) || 5 });
      toast.success("Review added");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleApprove = async (r) => {
    try {
      await api.put(`/admin/reviews/${r.id}`, { customer_name: r.customer_name, text: r.text, rating: r.rating, artist_slug: r.artist_slug || "", event_type: r.event_type || "", approved: !r.approved });
      load();
    } catch { toast.error("Could not update review"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete review by ${r.customer_name}?`)) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Review deleted");
      load();
    } catch { toast.error("Could not delete review"); }
  };

  const inputCls = "rounded-xl bg-ivory/5 border-ivory/15 text-ivory h-11";

  return (
    <div data-testid="admin-reviews-page">
      <SEO title="Reviews" description="The Velvet Brush admin." path="/admin/reviews" />
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-serif font-bold text-2xl sm:text-3xl">Reviews</h1>
          <p className="mt-1 text-sm text-ivory/50">Approved reviews appear on the homepage and artist profiles.</p>
        </div>
        <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="add-review-btn" className="rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold">
          <Plus className="w-4 h-4 mr-1" /> Add Review
        </Button>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        {reviews.map((r) => (
          <div key={r.id} data-testid={`admin-review-${r.id}`} className="bg-ivory/5 border border-ivory/10 rounded-2xl p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold">{r.customer_name}</p>
                <div className="flex gap-0.5 mt-1">
                  {Array.from({ length: r.rating }).map((_, j) => <Star key={j} className="w-3.5 h-3.5 text-gold fill-gold" />)}
                </div>
              </div>
              <button onClick={() => remove(r)} data-testid={`delete-review-${r.id}`} className="p-2 text-ivory/50 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
            <p className="mt-3 text-sm text-ivory/70 leading-relaxed">"{r.text}"</p>
            <div className="mt-4 pt-3 border-t border-ivory/10 flex items-center justify-between">
              <span className="text-xs text-ivory/40">{r.event_type || "General"}</span>
              <label className="flex items-center gap-2 text-xs text-ivory/50">
                <Switch checked={r.approved} onCheckedChange={() => toggleApprove(r)} data-testid={`approve-review-${r.id}`} />
                {r.approved ? "Approved" : "Hidden"}
              </label>
            </div>
          </div>
        ))}
        {reviews.length === 0 && <p className="text-sm text-ivory/40" data-testid="no-reviews">No reviews yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#262525] text-ivory border-ivory/10 max-w-lg" data-testid="review-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Add Review</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="flex flex-col gap-4 mt-2">
            <div>
              <Label>Customer Name *</Label>
              <Input required value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} data-testid="review-name-input" className={inputCls} />
            </div>
            <div>
              <Label>Review *</Label>
              <Textarea required value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} data-testid="review-text-input" className="rounded-xl bg-ivory/5 border-ivory/15 text-ivory min-h-[100px]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Rating (1–5)</Label>
                <Input type="number" min="1" max="5" value={form.rating} onChange={(e) => setForm((f) => ({ ...f, rating: e.target.value }))} data-testid="review-rating-input" className={inputCls} />
              </div>
              <div>
                <Label>Event Type</Label>
                <Input value={form.event_type} onChange={(e) => setForm((f) => ({ ...f, event_type: e.target.value }))} data-testid="review-event-input" className={inputCls} placeholder="Bridal Makeup" />
              </div>
            </div>
            <div>
              <Label>Artist</Label>
              <Select value={form.artist_slug || "all"} onValueChange={(v) => setForm((f) => ({ ...f, artist_slug: v === "all" ? "" : v }))}>
                <SelectTrigger data-testid="review-artist-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="review-artist-all">All / General</SelectItem>
                  {artists.map((a) => <SelectItem key={a.id} value={a.slug} data-testid={`review-artist-${a.slug}`}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm" data-testid="review-approved-label">
              <Switch checked={form.approved} onCheckedChange={(v) => setForm((f) => ({ ...f, approved: v }))} data-testid="review-approved-toggle" /> Approved (visible on site)
            </label>
            <Button type="submit" disabled={saving} data-testid="review-save-btn"
              className="rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold h-12">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Add Review"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
